/**
 * ============================================================================
 * markdownToTiptap — Converte markdown (CommonMark + GFM) em documento
 *                    Tiptap (ProseMirror JSON), preservando marks inline
 *                    (bold/italic/code/link/strike) e blocos GFM (tabelas,
 *                    task lists, alerts do estilo GitHub → callouts).
 *
 * Usado por:
 *   - `kb-import-md` (edge, Deno) — para importação de arquivos .md
 *   - Editor (browser) — para hidratar páginas legadas que só têm
 *     content_markdown
 *
 * Implementação: apoia-se em `marked` (lexer estável e minúsculo, funciona
 *                em Deno via npm: e no browser via bundler). Trata:
 *    - headings, paragraphs, listas (bullet/ordered/task), code blocks,
 *      blockquotes, horizontal rule, imagens, links, tabelas (GFM)
 *    - marcas: bold, italic, code, strike, link, del
 *    - GFM alerts (> [!NOTE|TIP|IMPORTANT|WARNING|CAUTION|INFO|SUCCESS|...])
 *      → node `callout` com variant mapeada
 *    - HTML `<details><summary>…</summary>…</details>` → node `toggle`
 *
 * CHANGELOG:
 *   RELEASE: 2026-07-08 v1.0.0 - Versão inicial.
 * ============================================================================
 */

import { marked, type Tokens } from "marked";
import type { PMNode } from "./tiptapToMarkdown";

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Faz decode de entidades HTML mais comuns geradas pelo marked. */
function decodeEntities(s: string): string {
  return s
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/&#x27;/g, "'")
    .replace(/&nbsp;/g, " ");
}

/** Cria um text node com marcas opcionais. */
function textNode(text: string, marks?: { type: string; attrs?: Record<string, unknown> }[]): PMNode {
  const node: PMNode = { type: "text", text };
  if (marks && marks.length > 0) node.marks = marks;
  return node;
}

// ---------------------------------------------------------------------------
// Inline tokens → text nodes com marks
// ---------------------------------------------------------------------------

function inlineTokensToNodes(
  tokens: Tokens.Generic[] | undefined,
  activeMarks: { type: string; attrs?: Record<string, unknown> }[] = [],
): PMNode[] {
  if (!tokens) return [];
  const out: PMNode[] = [];

  for (const t of tokens) {
    switch (t.type) {
      case "text": {
        // marked pode aninhar tokens dentro de "text"; se sim, recorre
        const nested = (t as Tokens.Text).tokens;
        if (nested && nested.length > 0) {
          out.push(...inlineTokensToNodes(nested, activeMarks));
        } else {
          out.push(textNode(decodeEntities((t as Tokens.Text).text), activeMarks));
        }
        break;
      }
      case "escape": {
        out.push(textNode(decodeEntities((t as Tokens.Escape).text), activeMarks));
        break;
      }
      case "strong": {
        out.push(
          ...inlineTokensToNodes((t as Tokens.Strong).tokens, [...activeMarks, { type: "bold" }]),
        );
        break;
      }
      case "em": {
        out.push(
          ...inlineTokensToNodes((t as Tokens.Em).tokens, [...activeMarks, { type: "italic" }]),
        );
        break;
      }
      case "codespan": {
        out.push(textNode(decodeEntities((t as Tokens.Codespan).text), [...activeMarks, { type: "code" }]));
        break;
      }
      case "del": {
        out.push(
          ...inlineTokensToNodes((t as Tokens.Del).tokens, [...activeMarks, { type: "strike" }]),
        );
        break;
      }
      case "link": {
        const link = t as Tokens.Link;
        out.push(
          ...inlineTokensToNodes(link.tokens, [
            ...activeMarks,
            { type: "link", attrs: { href: link.href, title: link.title ?? null } },
          ]),
        );
        break;
      }
      case "image": {
        const img = t as Tokens.Image;
        out.push({
          type: "image",
          attrs: { src: img.href, alt: img.text ?? "", title: img.title ?? null },
        });
        break;
      }
      case "br": {
        out.push({ type: "hardBreak" });
        break;
      }
      case "html": {
        // HTML inline (ex.: <u>, <sup>) — descarta tag e conserva texto
        const raw = (t as Tokens.HTML).text ?? "";
        const stripped = raw.replace(/<[^>]+>/g, "");
        if (stripped) out.push(textNode(decodeEntities(stripped), activeMarks));
        break;
      }
      default: {
        // Fallback: se tem `text`, joga como texto puro
        const raw = (t as { text?: string }).text;
        if (raw) out.push(textNode(decodeEntities(raw), activeMarks));
      }
    }
  }
  return out;
}

// ---------------------------------------------------------------------------
// Mapeia GitHub alert → variant do callout da KB
// ---------------------------------------------------------------------------
const ALERT_MAP: Record<string, string> = {
  NOTE: "info",
  TIP: "success",
  IMPORTANT: "warning",
  WARNING: "warning",
  CAUTION: "danger",
  INFO: "info",
  SUCCESS: "success",
  DANGER: "danger",
};

/**
 * Verifica se um blockquote é um GitHub alert (primeira linha `[!TIPO]`)
 * e devolve variant + tokens do restante do conteúdo, ou null se não for.
 */
function tryParseGithubAlert(bq: Tokens.Blockquote): { variant: string; tokens: Tokens.Generic[] } | null {
  const first = bq.tokens?.[0];
  if (!first || first.type !== "paragraph") return null;
  const para = first as Tokens.Paragraph;
  const raw = para.raw?.trim() ?? "";
  const m = raw.match(/^\[!(\w+)\]\s*\n?([\s\S]*)$/);
  if (!m) return null;
  const variant = ALERT_MAP[m[1].toUpperCase()] ?? "info";
  const rest = m[2].trim();

  // Re-lexa o restante do primeiro parágrafo + demais tokens do blockquote
  const remaining: Tokens.Generic[] = [];
  if (rest) {
    const reLexed = marked.lexer(rest) as Tokens.Generic[];
    remaining.push(...reLexed);
  }
  for (let i = 1; i < (bq.tokens?.length ?? 0); i++) remaining.push(bq.tokens![i]);
  return { variant, tokens: remaining };
}

// ---------------------------------------------------------------------------
// Block tokens → nós Tiptap
// ---------------------------------------------------------------------------

function blockTokensToNodes(tokens: Tokens.Generic[]): PMNode[] {
  const out: PMNode[] = [];
  for (const t of tokens) {
    switch (t.type) {
      case "heading": {
        const h = t as Tokens.Heading;
        out.push({
          type: "heading",
          attrs: { level: Math.min(6, Math.max(1, h.depth)) },
          content: inlineTokensToNodes(h.tokens),
        });
        break;
      }
      case "paragraph": {
        const p = t as Tokens.Paragraph;
        out.push({ type: "paragraph", content: inlineTokensToNodes(p.tokens) });
        break;
      }
      case "space":
        // Ignora — separadores são resolvidos pela lógica de blocos
        break;
      case "hr":
        out.push({ type: "horizontalRule" });
        break;
      case "blockquote": {
        const bq = t as Tokens.Blockquote;
        const alert = tryParseGithubAlert(bq);
        if (alert) {
          out.push({
            type: "callout",
            attrs: { variant: alert.variant },
            content: blockTokensToNodes(alert.tokens),
          });
        } else {
          out.push({
            type: "blockquote",
            content: blockTokensToNodes(bq.tokens ?? []),
          });
        }
        break;
      }
      case "code": {
        const c = t as Tokens.Code;
        out.push({
          type: "codeBlock",
          attrs: c.lang ? { language: c.lang } : {},
          content: c.text ? [textNode(c.text)] : [],
        });
        break;
      }
      case "list": {
        const list = t as Tokens.List;
        const isTask = list.items.every((it) => it.task);
        const nodeType = isTask ? "taskList" : list.ordered ? "orderedList" : "bulletList";
        const start = list.ordered ? Number(list.start ?? 1) : undefined;

        const items: PMNode[] = list.items.map((item) => {
          const itemType = isTask ? "taskItem" : "listItem";
          const attrs = isTask ? { checked: Boolean(item.checked) } : undefined;
          const content = blockTokensToNodes(item.tokens ?? []);
          // Se o listItem só tem um parágrafo, o Tiptap aceita — mas mantemos
          // paragraphs explícitos para consistência.
          return {
            type: itemType,
            ...(attrs ? { attrs } : {}),
            content: content.length > 0 ? content : [{ type: "paragraph" }],
          };
        });

        out.push({
          type: nodeType,
          ...(start !== undefined && start !== 1 ? { attrs: { start } } : {}),
          content: items,
        });
        break;
      }
      case "table": {
        const tbl = t as Tokens.Table;
        const rows: PMNode[] = [];
        // Header
        rows.push({
          type: "tableRow",
          content: tbl.header.map((cell) => ({
            type: "tableHeader",
            content: [{ type: "paragraph", content: inlineTokensToNodes(cell.tokens) }],
          })),
        });
        // Body
        for (const row of tbl.rows) {
          rows.push({
            type: "tableRow",
            content: row.map((cell) => ({
              type: "tableCell",
              content: [{ type: "paragraph", content: inlineTokensToNodes(cell.tokens) }],
            })),
          });
        }
        out.push({ type: "table", content: rows });
        break;
      }
      case "html": {
        // <details><summary>...</summary>...</details> → toggle
        const raw = (t as Tokens.HTML).text ?? "";
        const m = raw.match(/<details[^>]*>\s*<summary>([\s\S]*?)<\/summary>([\s\S]*?)<\/details>/i);
        if (m) {
          const summary = decodeEntities(m[1].trim().replace(/<[^>]+>/g, ""));
          const innerMd = m[2].trim();
          const innerNodes = innerMd
            ? blockTokensToNodes(marked.lexer(innerMd) as Tokens.Generic[])
            : [{ type: "paragraph" }];
          out.push({ type: "toggle", attrs: { summary }, content: innerNodes });
        } else {
          // HTML solto: transforma em parágrafo com texto extraído
          const stripped = raw.replace(/<[^>]+>/g, "").trim();
          if (stripped) {
            out.push({
              type: "paragraph",
              content: [textNode(decodeEntities(stripped))],
            });
          }
        }
        break;
      }
      default: {
        // Fallback: se for inline solto, embrulha em parágrafo
        const anyTok = t as Tokens.Generic & { tokens?: Tokens.Generic[]; text?: string };
        if (anyTok.tokens) {
          out.push({
            type: "paragraph",
            content: inlineTokensToNodes(anyTok.tokens),
          });
        } else if (anyTok.text) {
          out.push({ type: "paragraph", content: [textNode(decodeEntities(anyTok.text))] });
        }
      }
    }
  }
  return out;
}

/**
 * Ponto de entrada público. Recebe markdown e devolve um documento
 * Tiptap pronto para gravar em content_json.
 */
export function markdownToTiptap(md: string): { type: "doc"; content: PMNode[] } {
  const clean = (md ?? "").replace(/\r\n?/g, "\n");
  if (!clean.trim()) return { type: "doc", content: [{ type: "paragraph" }] };

  // Configura marked: GFM ligado, breaks preservados fica opcional
  marked.setOptions({ gfm: true });
  const tokens = marked.lexer(clean) as Tokens.Generic[];
  const nodes = blockTokensToNodes(tokens);
  return {
    type: "doc",
    content: nodes.length > 0 ? nodes : [{ type: "paragraph" }],
  };
}
